import { ENV } from "@/config/environment";
import {
  authenticatedFetch,
  getCurrentUser,
  type AuthenticatedFetchOptions,
} from "@/services/auth";
import { BackendError } from "@/services/backend";
import type { ApiResponse, PagedResponse } from "@/types/api";

const BASE = ENV.backendUrl;

export type NotificationStatus =
  | "NEW"
  | "DELIVERED"
  | "READ"
  | "ARCHIVED"
  | "ACTIONED"
  | "DISMISSED";

export type NotificationDeliveryStatus =
  | "PENDING"
  | "SENDING"
  | "SENT"
  | "FAILED_RETRYABLE"
  | "FAILED_PERMANENT"
  | "CANCELLED"
  | "SKIPPED";

export type EmailNotification = {
  id: string;
  tenantId: string;
  messageId: string;
  category: string;
  workflowRunId?: string | null;
  projectId?: string | null;
  documentId?: string | null;
  senderEmail: string;
  senderName: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string | null;
  recipients: Array<{ email: string; name?: string }>;
  attachments?: Array<{
    fileName: string;
    storageKey: string;
    mimeType: string;
    sizeBytes: number;
  }>;
  status: NotificationStatus;
  deliveryStatus: NotificationDeliveryStatus;
  statusChangedAt?: string | null;
  deliveredAt?: string | null;
  readAt?: string | null;
  archivedAt?: string | null;
  actionedAt?: string | null;
  dismissedAt?: string | null;
  actionTaken?: string | null;
  threadId?: string | null;
  priority: "LOW" | "NORMAL" | "HIGH" | "URGENT";
  recipientUserId?: string | null;
  recipientEmail?: string | null;
  attemptCount: number;
  nextAttemptAt?: string | null;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
  lastError?: string | null;
  externalMessageId?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  sentAt?: string | null;
  dispatchLog: EmailDispatchLog[];
};

export type NotificationInboxItem = {
  notification: EmailNotification;
  threadCount: number;
};

export type NotificationPreference = {
  id?: string | null;
  notificationType: string;
  channels: Record<"inapp" | "email" | "push" | "sms", boolean>;
  priorityThreshold: "LOW" | "NORMAL" | "HIGH" | "URGENT";
};

export type NotificationDigestPreference = {
  userId: string;
  enabled: boolean;
  frequency: "DAILY" | "WEEKLY" | "DISABLED";
  sendAtHour: number;
  timezone: string;
  channel: "EMAIL" | "INAPP";
  minPriority: "LOW" | "NORMAL" | "HIGH" | "URGENT";
  excludeTypes: string[];
};

export type PushSubscriptionInfo = {
  id: string;
  endpoint: string;
  createdAt: string;
  lastUsedAt?: string | null;
};

export type EmailDispatchLog = {
  id: string;
  attemptNumber: number;
  gatewayProvider: string;
  requestPayload?: unknown;
  responseStatus?: number | null;
  responseBody?: string | null;
  externalMessageId?: string | null;
  durationMs?: number | null;
  success: boolean;
  errorCode?: string | null;
  errorMessage?: string | null;
  occurredAt: string;
};

export type NotificationTemplate = {
  category: string;
  locale: string;
  name: string;
  description?: string | null;
  subject: string;
  bodyHtml: string;
  requiredVariables: string[];
  enabled: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  overrideId?: string | null;
  tenantId?: string | null;
  overrideSubject?: string | null;
  overrideBodyHtml?: string | null;
  bannerColor?: string | null;
  logoOverride?: string | null;
  overrideEnabled?: boolean | null;
  overrideUpdatedAt?: string | null;
};

export type EmailTemplateVariable = {
  key: string;
  token: string;
  description: string;
  example: string;
};

export type NotificationListParams = {
  page?: number;
  size?: number;
  tenantId?: string;
  category?: string;
  status?: string;
  deliveryStatus?: string;
  recipientEmail?: string;
  workflowRunId?: string;
  projectId?: string;
  documentId?: string;
  from?: string;
  to?: string;
  search?: string;
};

/**
 * Etiquetas amigables en español para los `notificationType` (códigos técnicos
 * del catálogo, p.ej. "document.shared"). Cubre los tipos sembrados por defecto
 * en el backend (NotificationPreferenceService.DEFAULT_TYPES) más los del
 * NotificationTypeRegistry. Para tipos no mapeados se usa
 * {@link notificationTypeLabel}, que formatea el código de forma legible y
 * conserva siempre el valor técnico como subtexto/tooltip.
 */
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  // Documentos
  "document.shared": "Documento compartido",
  "document.assigned": "Documento asignado",
  "document.mention": "Mención en documento",
  "document.comment.created": "Comentario en documento",
  "document.comment.reply": "Respuesta a comentario",
  "document.version.created": "Nueva versión de documento",
  "document.version.published": "Versión de documento publicada",
  "document.render.ready": "Documento generado listo",
  "mention.created": "Te mencionaron",
  // Identidad / acceso
  "identity.user.invited": "Invitación de usuario",
  "identity.user.activation": "Activación de cuenta",
  "identity.user.affiliated": "Afiliación a organización",
  "identity.user.welcome": "Bienvenida",
  "identity.user.temp_password": "Contraseña temporal",
  "identity.password.set": "Definir contraseña",
  "identity.password.reset": "Restablecer contraseña",
  "identity.password.changed": "Contraseña actualizada",
  "identity.mfa.enabled": "Doble factor activado",
  "identity.mfa.disabled": "Doble factor desactivado",
  "identity.account.locked": "Cuenta bloqueada",
  "identity.role.changed": "Rol actualizado",
  "resource.access.revoked": "Acceso revocado",
  // Plantillas / aprobaciones
  "template.approval.requested": "Aprobación de plantilla solicitada",
  "template.approval.decided": "Aprobación de plantilla resuelta",
  "template.version.activated": "Versión de plantilla activada",
  // Workflow / flujos
  "workflow.task.assigned": "Tarea de flujo asignada",
  "workflow.task.escalated": "Tarea de flujo escalada",
  "workflow.sla.breached": "SLA de flujo incumplido",
  "workflow.run.completed": "Flujo completado",
  "workflow.completed": "Flujo completado",
  "workflow.failed": "Flujo con error",
  // Proyecto / tareas / expediente
  "project.task.assigned": "Tarea asignada",
  "project.task.reassigned": "Tarea reasignada",
  "project.task.overdue": "Tarea vencida",
  "project.task.completed": "Tarea completada",
  "project.status.changed": "Estado de expediente actualizado",
  "project.team.updated": "Equipo del expediente actualizado",
  "matter.assigned": "Expediente asignado",
  "task.assigned": "Tarea asignada",
  // Firma
  "signature.requested": "Firma solicitada",
  "signature.signed": "Documento firmado",
  "signature.completed": "Firma completada",
  "signature.declined": "Firma rechazada",
  "signature.expired": "Firma expirada",
  "contract.signed": "Contrato firmado",
  "nda.reviewed": "NDA revisado",
  // Contrapartes
  "counterparty.onboarding.completed": "Onboarding de contraparte completado",
  // Riesgo / reportes / digest
  "risk.assigned": "Riesgo asignado",
  "risk.high.detected": "Riesgo alto detectado",
  "risk.high_flagged": "Riesgo alto marcado",
  "report.scheduled.ready": "Reporte programado listo",
  "ai.budget.exceeded": "Presupuesto de IA superado",
  "digest.daily": "Resumen diario",
  "digest.weekly": "Resumen semanal",
  "notification.digest": "Resumen de notificaciones",
  // Genérico
  generic: "Notificación general",
};

/**
 * Convierte un `notificationType` técnico en un fallback legible: separa por
 * puntos/guiones/guiones bajos y capitaliza cada palabra
 * (p.ej. "workflow.run.completed" → "Workflow run completed").
 */
function humanizeNotificationType(notificationType: string): string {
  const words = notificationType
    .split(/[._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (words.length === 0) return notificationType;
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Devuelve la etiqueta amigable de un `notificationType`. Si no hay mapeo
 * explícito, recurre al fallback {@link humanizeNotificationType}. Nunca pierde
 * el valor técnico: para mostrarlo usa {@link NOTIFICATION_TYPE_LABELS} junto
 * con el propio `notificationType` en un subtexto/tooltip.
 */
export function notificationTypeLabel(notificationType: string): string {
  const key = notificationType.trim();
  return NOTIFICATION_TYPE_LABELS[key] ?? humanizeNotificationType(key);
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      sp.set(key, String(value));
  });
  const query = sp.toString();
  return query ? `?${query}` : "";
}

async function call<T>(
  path: string,
  init?: RequestInit,
  opts?: AuthenticatedFetchOptions,
): Promise<T> {
  const res = await authenticatedFetch(`${BASE}${path}`, init, opts);
  if (res.status === 204) return null as T;
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.success)
    throw new BackendError(
      json.error ?? "unknown error",
      json.code,
      res.status,
    );
  return json.data as T;
}

function adminOptions(): AuthenticatedFetchOptions | undefined {
  return getCurrentUser()?.roles.includes("SUPERADMIN")
    ? { tenantIdOverride: "*" }
    : undefined;
}

function json(method: "POST" | "PUT" | "PATCH", body?: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  };
}

export const notificationsApi = {
  listAdmin: (params: NotificationListParams = {}) =>
    call<PagedResponse<EmailNotification>>(
      `/admin/notifications${qs(params)}`,
      undefined,
      adminOptions(),
    ),
  listForTenant: (tenantId: string, params: NotificationListParams = {}) =>
    call<PagedResponse<EmailNotification>>(
      `/tenants/${tenantId}/notifications${qs(params)}`,
    ),
  inbox: (
    params: NotificationListParams & {
      type?: string;
      priority?: string;
      groupBy?: string;
    } = {},
  ) =>
    call<PagedResponse<NotificationInboxItem>>(
      `/notifications/inbox${qs(params)}`,
    ),
  unreadCount: () => call<{ count: number }>("/notifications/unread-count"),
  updateStatus: (
    id: string,
    body: {
      status: NotificationStatus;
      actionTaken?: string;
      channel?: string;
    },
  ) =>
    call<EmailNotification>(`/notifications/${id}/status`, json("PATCH", body)),
  bulkAction: (ids: string[], action: "MARK_READ" | "ARCHIVE" | "DISMISS") =>
    call<EmailNotification[]>(
      "/notifications/bulk-action",
      json("POST", { ids, action }),
    ),
  getAdmin: (id: string) =>
    call<EmailNotification>(
      `/admin/notifications/${id}`,
      undefined,
      adminOptions(),
    ),
  getForTenant: (tenantId: string, id: string) =>
    call<EmailNotification>(`/tenants/${tenantId}/notifications/${id}`),
  retryAdmin: (id: string) =>
    call<EmailNotification>(
      `/admin/notifications/${id}/resend`,
      {
        method: "POST",
      },
      adminOptions(),
    ),
  retryForTenant: (tenantId: string, id: string) =>
    call<EmailNotification>(`/tenants/${tenantId}/notifications/${id}/retry`, {
      method: "POST",
    }),
  cancelAdmin: (id: string) =>
    call<EmailNotification>(
      `/admin/notifications/${id}/cancel`,
      {
        method: "POST",
      },
      adminOptions(),
    ),
  sendTestAdmin: (body: {
    tenantId?: string;
    recipientEmail: string;
    recipientName?: string;
    category?: string;
    variables?: Record<string, unknown>;
  }) =>
    call<EmailNotification>(
      "/admin/notifications/test",
      json("POST", body),
      adminOptions(),
    ),
  catalog: {
    list: () =>
      call<{ catalog: NotificationTemplate[] }>("/admin/email-templates"),
    get: (category: string) =>
      call<NotificationTemplate>(
        `/admin/email-templates/${encodeURIComponent(category)}`,
      ),
    update: (
      category: string,
      body: {
        locale?: string;
        subject?: string;
        bodyHtml?: string;
        description?: string;
        enabled?: boolean;
        requiredVariables?: string[];
      },
    ) =>
      call<NotificationTemplate>(
        `/admin/email-templates/${encodeURIComponent(category)}`,
        json("PUT", body),
      ),
    variables: (category: string) =>
      call<EmailTemplateVariable[]>(
        `/admin/email-templates/${encodeURIComponent(category)}/variables`,
      ),
    allVariables: () =>
      call<EmailTemplateVariable[]>("/admin/email-templates/variables"),
    preview: (
      category: string,
      variables?: Record<string, unknown>,
      tenantId?: string,
    ) =>
      call<{ subject: string; html: string }>(
        `/admin/email-templates/${encodeURIComponent(category)}/preview${qs({ tenantId })}`,
        json("POST", { variables }),
      ),
    test: (
      category: string,
      body: {
        tenantId: string;
        recipientEmail: string;
        recipientName?: string;
        variables?: Record<string, unknown>;
      },
    ) =>
      call<EmailNotification>(
        `/admin/email-templates/${encodeURIComponent(category)}/test`,
        json("POST", body),
      ),
  },
  preferences: {
    list: () => call<NotificationPreference[]>("/notifications/preferences"),
    update: (
      body: Array<
        Pick<
          NotificationPreference,
          "notificationType" | "channels" | "priorityThreshold"
        >
      >,
    ) =>
      call<NotificationPreference[]>(
        "/notifications/preferences",
        json("PUT", body),
      ),
  },
  digest: {
    get: () =>
      call<NotificationDigestPreference>("/notifications/digest/preferences"),
    update: (body: Partial<NotificationDigestPreference>) =>
      call<NotificationDigestPreference>(
        "/notifications/digest/preferences",
        json("PUT", body),
      ),
  },
  push: {
    list: () =>
      call<PushSubscriptionInfo[]>("/notifications/push-subscriptions"),
    /**
     * Clave pública VAPID para `pushManager.subscribe`. `publicKey` puede ser
     * null si el backend no tiene configurado el proveedor de push.
     */
    vapidPublicKey: () =>
      call<{ publicKey: string | null }>(
        "/notifications/push/vapid-public-key",
      ),
    subscribe: (body: {
      endpoint: string;
      p256dhKey: string;
      authKey: string;
      userAgent?: string;
    }) =>
      call<PushSubscriptionInfo>(
        "/notifications/push-subscriptions",
        json("POST", body),
      ),
  },
};
